import AvatarCompany from "@/components/company/avatar-company";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import { PopoverContent } from "@/components/ui/popover";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CompanyDTO } from "@shaarp/common";
import { Loader2Icon, SearchIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const SEARCH_DEBOUNCE_MS = 250;
const RESULTS_LIMIT = 20;

type CompanyContextTab = "lists" | "recent";

const TAB_ENDPOINTS: Record<CompanyContextTab, string> = {
  lists: "/listing/companies",
  recent: "/my/user/history/companies",
};

/**
 * Picker to attach a saved company as context to the chat input, with two tabs:
 * companies from the user's lists and recently visited ones. Search is performed
 * server-side per tab.
 *
 * The render surface is dictated by the caller via `variant`, not self-detected:
 * `"popover"` returns a bare `PopoverContent` that MUST be mounted inside a
 * `<Popover>` (desktop), while `"sheet"` returns a self-contained bottom sheet
 * (mobile). Relying on a device hook here would render `PopoverContent` during
 * the hook's initial `undefined` window even at the mobile call site — which has
 * no `<Popover>` ancestor — and crash with "PopoverPortal must be used within
 * Popover".
 */
export default function ChatCompanyContextPicker({
  open,
  onOpenChange,
  onSelect,
  variant,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (company: CompanyDTO) => void;
  variant: "popover" | "sheet";
}) {
  const api = useApi();
  const toastError = useToastError();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<CompanyContextTab>("recent");
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState<CompanyDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Discards responses from searches that resolved after a newer one was fired.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setActiveTab("recent");
      setCompanies([]);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ limit: String(RESULTS_LIMIT) });
        if (search.trim()) params.set("name", search.trim());
        const { data } = await api.get<CompanyDTO[]>(
          `${TAB_ENDPOINTS[activeTab]}?${params.toString()}`
        );
        if (requestId === requestIdRef.current) setCompanies(data);
      } catch {
        if (requestId === requestIdRef.current) {
          setCompanies([]);
          toastError();
        }
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [open, search, activeTab, api, toastError]);

  const handleSelect = (company: CompanyDTO) => {
    onSelect(company);
    onOpenChange(false);
  };

  const content = (
    <div className="flex flex-col min-h-0">
      <div className="flex items-center gap-2 px-3 border-b">
        <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("chat.input.companyContext.searchPlaceholder")}
          className="flex w-full h-11 bg-transparent text-base md:text-sm outline-none placeholder:text-muted-foreground"
          autoFocus={variant === "popover"}
        />
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as CompanyContextTab)}
        className="px-3 pt-3"
      >
        <TabsList className="w-full">
          <TabsTrigger value="recent" className="flex-1">
            {t("chat.input.companyContext.tabs.recentlyVisited")}
          </TabsTrigger>
          <TabsTrigger value="lists" className="flex-1">
            {t("chat.input.companyContext.tabs.inLists")}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="overflow-y-auto flex-1 p-1 min-h-0">
        {isLoading && (
          <div className="flex gap-2 justify-center items-center py-6 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" />
            {t("chat.input.companyContext.loading")}
          </div>
        )}
        {!isLoading && companies.length === 0 && (
          <div className="py-6 text-sm text-center text-muted-foreground">
            {activeTab === "recent"
              ? t("chat.input.companyContext.emptyRecent")
              : t("chat.input.companyContext.empty")}
          </div>
        )}
        {!isLoading &&
          companies.map((company) => (
            <button
              key={company.id}
              type="button"
              onClick={() => handleSelect(company)}
              className="flex gap-3 items-center px-2 py-2 w-full text-left rounded-md transition-colors hover:bg-muted"
            >
              <AvatarCompany
                company={company}
                className="border size-7 bg-muted"
              />
              <div className="flex flex-col min-w-0">
                <span className="truncate">{company.name}</span>
                {company.domain && (
                  <span className="text-xs truncate text-muted-foreground">
                    {company.domain}
                  </span>
                )}
              </div>
            </button>
          ))}
      </div>
    </div>
  );

  if (variant === "sheet") {
    return (
      <MobileSheet open={open} onOpenChange={onOpenChange} heightClass="h-[80dvh]">
        <SheetTitle className="px-4 pt-2 text-lg font-semibold">
          {t("chat.input.companyContext.title")}
        </SheetTitle>
        <SheetDescription className="sr-only">
          {t("chat.input.companyContext.description")}
        </SheetDescription>
        <div className="flex flex-col flex-1 min-h-0">{content}</div>
      </MobileSheet>
    );
  }

  return (
    <PopoverContent
      align="start"
      side="top"
      sideOffset={8}
      className={cn("w-[420px] h-[440px] p-0 overflow-hidden flex flex-col")}
    >
      {content}
    </PopoverContent>
  );
}
